import InlineWorker from 'inline-worker'
import Resampler from './resampler'
import i18n from '@/locales'
export class Recorder {
  config = {
    bufferLen: 4096,
    numChannels: 1, // 默认单声道
    onaudioprocess: null,
    canvas: 'canvas',
  }

  recording = false

  callbacks = {
    getBuffer: [],
    encodeSpeex: [],
    exportWAV: [],
  }

  constructor(source, cfg) {
    Object.assign(this.config, cfg)
    this.context = source.context
    this.analyserNode = this.context.createAnalyser()
    this.analyserNode.fftSize = 2048
    this.analyserNode.minDecibels = -90
    this.analyserNode.maxDecibels = -10
    this.analyserNode.smoothingTimeConstant = 0.85
    const bufferLength = this.analyserNode.frequencyBinCount
    this.waveArray = new Uint8Array(bufferLength)
    this.analyserNode.getByteTimeDomainData(this.waveArray)
    this.getSpeexData = this.config.getSpeexData
    this.node = (
      this.context.createScriptProcessor || this.context.createJavaScriptNode
    ).call(
      this.context,
      this.config.bufferLen,
      this.config.numChannels,
      this.config.numChannels
    )

    this.resampler = new Resampler(
      this.context.sampleRate,
      this.config.sampleRate,
      this.config.numChannels,
      this.node.bufferSize
    )

    this.node.onaudioprocess = event => {
      if (!this.recording) {
        return false
      }
      let buffers = []
      for (let channel = 0; channel < this.config.numChannels; channel++) {
        let inputBuffer = event.inputBuffer.getChannelData(channel)
        let samples
        if (this.context.sampleRate != this.config.sampleRate) {
          samples = this.resampler.resampler(inputBuffer)
        } else {
          samples = inputBuffer
        }
        buffers.push(samples)
      }
      // 发送给worker
      this.worker.postMessage({
        command: 'record',
        buffer: buffers,
      })
      if (this.config.onaudioprocess) {
        this.config.onaudioprocess(buffers)
      }
    }

    source.connect(this.node)
    source.connect(this.analyserNode)

    this.worker = new InlineWorker(function() {
      let recording
      let recLength = 0,
        recBuffers = [],
        sampleRate,
        numChannels
      self.onmessage = function(e) {
        switch (e.data.command) {
          case 'init':
            init(e.data.config)
            break
          case 'record':
            record(e.data.buffer)
            break
          case 'encodeSpeex':
            encodeSpeex()
            break
          case 'exportWAV':
            exportWAV(e.data.type)
            break
          case 'getBuffer':
            getBuffer()
            break
          case 'clear':
            clear()
            break
          case 'setRecording':
            setRecording(e.data.status)
            break
        }
      }

      // 不再加载 public/plugins/speex.min.js：ssb 声明的是 aue:"raw"，发给讯飞的一直是
      // 16bit PCM，speex 编码结果从来没被用到（旧代码每拍白编码 10 页还各做一次同步
      // FileReaderSync）。连带那个 emscripten 大包也不用在每次建录音器时下载了。
      function init(config) {
        sampleRate = config.sampleRate
        numChannels = config.numChannels
        desiredSamplingRate = config.desiredSamplingRate
        initBuffers()
      }

      function record(inputBuffer) {
        for (var channel = 0; channel < numChannels; channel++) {
          recBuffers[channel].push(inputBuffer[channel])
        }
        recLength += inputBuffer[0].length
      }

      function setRecording(status) {
        recording = status
      }
      /**
       * 把录音按 200ms 一帧推给上层（sndmsc 再 base64 后经 WebSocket 发给讯飞）。
       *
       * 旧实现是一个「棘轮」：setInterval(200ms) 每拍只消费固定 3200 采样（=200ms 音频），
       * 恰好等于声卡的产出速率，而 setInterval 只会晚不会早 —— 任何一次漏拍都变成永久
       * 欠账、永不回补；停止时再把全部欠账当成「一帧」发出去（还重复发了一遍）。
       * 线上实测一次**成功**的 120 秒朗读篇章：尾帧已达 12117B，占讯飞单帧上限 19200B
       * 的 63%，最大帧间隔 412ms（漏了 1 拍）—— 每漏一拍尾帧 +6400B，再漏 2 拍就越限。
       * 120 秒 = 600 拍，只要 0.5% 的拍被漏掉就失败，这正是「只有朗读篇章挂、多设备都挂、
       * 又不是每次必现」的原因（失败时服务端报 60114 time out）。
       *
       * 改法：每拍把已积累的数据**全部排空**，内部按 3200 分块、一块一帧。漏拍只会让
       * 下一拍多发几帧，不再累积；停止时同样分块排空，最后一块标记 status=true 作为结束帧，
       * 所以尾帧恒 ≤6400B。
       *
       * 另外去掉了每拍那 10 次 speex 编码 + 10 次同步 FileReaderSync：ssb 声明的是
       * aue:"raw"，真正发出去的一直是 view 里的 16bit PCM，spxdata 从来没被用过
       * （sndmsc 里那句 concat 还把它丢了）。这是每拍 8ms 左右的纯浪费，删掉它本身
       * 就降低了漏拍概率。
       *
       * 只支持单/双声道中的单声道流式推送（numChannels 由 sndmsc 固定为 1）；
       * 双声道会按原逻辑交错后发出，未在本项目中使用。
       */
      function encodeSpeex() {
        const ENCODE_BUFFER_LENGTH = 3200
        // 顺序游标：按块读取，避免每拍都 mergeBuffers 整段录音
        // （120 秒时那是每 200ms 拷贝 7.3MB，本身就是漏拍的来源）
        let readChunk = 0
        let readOffset = 0
        let consumed = 0
        let finished = false

        // 各声道的分块是成对 push 的（见 record()），所以共用一组游标
        function pull(maxLen) {
          const out = []
          for (let ch = 0; ch < numChannels; ch++) out.push(new Float32Array(maxLen))
          let filled = 0
          while (filled < maxLen && readChunk < recBuffers[0].length) {
            const len = recBuffers[0][readChunk].length
            const take = Math.min(len - readOffset, maxLen - filled)
            for (let ch = 0; ch < numChannels; ch++) {
              out[ch].set(recBuffers[ch][readChunk].subarray(readOffset, readOffset + take), filled)
            }
            filled += take
            readOffset += take
            if (readOffset >= len) {
              readChunk++
              readOffset = 0
            }
          }
          consumed += filled
          const trimmed = out.map((b) => (filled === maxLen ? b : b.subarray(0, filled)))
          return numChannels === 2 ? interleave(trimmed[0], trimmed[1]) : trimmed[0]
        }

        function post(samples, isComplate) {
          const view = new DataView(new ArrayBuffer(samples.length * 2))
          floatTo16BitPCM(view, 0, samples)
          self.postMessage({
            command: 'getSpeexData',
            data: {
              status: isComplate,
              view: view,
            },
          })
        }

        const encodeItimer = setInterval(() => {
          if (finished) return
          if (recording == true) {
            // 录音中：排空所有整块，不满一块的零头留给下一拍
            while (recLength - consumed >= ENCODE_BUFFER_LENGTH) {
              post(pull(ENCODE_BUFFER_LENGTH), false)
            }
            return
          }
          // 已停止：分块排空剩余数据，最后一块作为结束帧
          finished = true
          clearInterval(encodeItimer)
          let remain = recLength - consumed
          if (remain <= 0) {
            // 停止时刚好没有零头：仍然要把结束帧发出去，否则服务端会一直等到超时
            post(new Float32Array(0), true)
            return
          }
          while (remain > 0) {
            const take = Math.min(remain, ENCODE_BUFFER_LENGTH)
            remain -= take
            post(pull(take), remain === 0)
          }
        }, 200)
      }

      /**
       * 导出wav
       * @param type
       * @param desiredSamplingRate 期望的采样率
       */
      function exportWAV(type) {
        var buffers = []
        for (var channel = 0; channel < numChannels; channel++) {
          var buffer = mergeBuffers(recBuffers[channel], recLength)
          // // 需要转换采样率
          // if (desiredSamplingRate != sampleRate) {
          //   // 插值去点
          //   buffer = interpolateArray(buffer, desiredSamplingRate, sampleRate)
          // }
          buffers.push(buffer)
        }
        var interleaved =
          numChannels === 2 ? interleave(buffers[0], buffers[1]) : buffers[0]
        var dataview = encodeWAV(interleaved, desiredSamplingRate)
        var audioBlob = new Blob([dataview], {
          type: type,
        })
        self.postMessage({
          command: 'exportWAV',
          data: audioBlob,
        })
      }
      /**
       * 转换采样率
       * @param data
       * @param newSampleRate 目标采样率
       * @param oldSampleRate 原始数据采样率
       * @returns {any[]|Array}
       */
      function interpolateArray(data, newSampleRate, oldSampleRate) {
        var fitCount = Math.round(data.length * (newSampleRate / oldSampleRate))
        var newData = new Array()
        var springFactor = new Number((data.length - 1) / (fitCount - 1))
        newData[0] = data[0] // for new allocation
        for (var i = 1; i < fitCount - 1; i++) {
          var tmp = i * springFactor
          var before = new Number(Math.floor(tmp)).toFixed()
          var after = new Number(Math.ceil(tmp)).toFixed()
          var atPoint = tmp - before
          newData[i] = linearInterpolate(data[before], data[after], atPoint)
        }
        newData[fitCount - 1] = data[data.length - 1] // for new allocation
        return newData
      }

      function linearInterpolate(before, after, atPoint) {
        return before + (after - before) * atPoint
      }

      function getBuffer() {
        var buffers = []
        for (var channel = 0; channel < numChannels; channel++) {
          var buffer = mergeBuffers(recBuffers[channel], recLength)
          buffers.push(buffer)
        }
        self.postMessage({
          command: 'getBuffer',
          data: buffers,
        })
      }

      function clear() {
        recLength = 0
        recBuffers = []
        initBuffers()
      }

      function initBuffers() {
        for (let channel = 0; channel < numChannels; channel++) {
          recBuffers[channel] = []
        }
      }

      function mergeBuffers(recBuffers, recLength) {
        let result = new Float32Array(recLength)
        let offset = 0
        for (let i = 0; i < recBuffers.length; i++) {
          result.set(recBuffers[i], offset)
          offset += recBuffers[i].length
        }
        return result
      }

      function interleave(inputL, inputR) {
        let length = inputL.length + inputR.length
        let result = new Float32Array(length)

        let index = 0,
          inputIndex = 0

        while (index < length) {
          result[index++] = inputL[inputIndex]
          result[index++] = inputR[inputIndex]
          inputIndex++
        }
        return result
      }

      function floatTo16BitPCM(output, offset, input) {
        for (let i = 0; i < input.length; i++, offset += 2) {
          let s = Math.max(-1, Math.min(1, input[i]))
          output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
        }
      }

      function writeString(view, offset, string) {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i))
        }
      }

      /**
       * 编码为wav
       * @param samples 采样点
       * @param sampleRate 采样率
       * @returns {DataView}
       */
      function encodeWAV(samples, sampleRate) {
        let buffer = new ArrayBuffer(44 + samples.length * 2)
        let view = new DataView(buffer)

        /* RIFF identifier */
        writeString(view, 0, 'RIFF')
        /* RIFF chunk length */
        view.setUint32(4, 36 + samples.length * 2, true)
        /* RIFF type */
        writeString(view, 8, 'WAVE')
        /* format chunk identifier */
        writeString(view, 12, 'fmt ')
        /* format chunk length */
        view.setUint32(16, 16, true)
        /* sample format (raw) */
        view.setUint16(20, 1, true)
        /* channel count */
        view.setUint16(22, numChannels, true)
        /* sample rate */
        view.setUint32(24, sampleRate, true)
        /* byte rate (sample rate * block align) */
        view.setUint32(28, sampleRate * 4, true)
        /* block align (channel count * bytes per sample) */
        view.setUint16(32, numChannels * 2, true)
        /* bits per sample */
        view.setUint16(34, 16, true)
        /* data chunk identifier */
        writeString(view, 36, 'data')
        /* data chunk length */
        view.setUint32(40, samples.length * 2, true)

        floatTo16BitPCM(view, 44, samples)

        return view
      }
    }, self)

    this.worker.postMessage({
      command: 'init',
      config: {
        sampleRate: this.context.sampleRate,
        desiredSamplingRate: this.config.sampleRate,
        numChannels: this.config.numChannels,
        origin: window.location.protocol + '//' + window.location.host,
      },
    })

    this.worker.onmessage = e => {
      if (e.data.command == 'getSpeexData') {
        this.getSpeexData && this.getSpeexData(e.data.data)
      } else {
        let cb = this.callbacks[e.data.command].pop()
        if (typeof cb == 'function') {
          cb(e.data.data)
        }
      }
    }
  }

  record() {
    this.node.connect(this.context.destination)
    this.recording = true
    this.worker.postMessage({
      command: 'setRecording',
      status: true,
    })
    this.worker.postMessage({
      command: 'encodeSpeex',
    })
  }

  stop() {
    this.worker.postMessage({
      command: 'setRecording',
      status: false,
    })
    this.recording = false
  }

  close() {
    if (this.context) {
      console.log(this.node)
      this.node.disconnect()
      this.context.close().then(() => {
        console.log('close')
        this.context = null
      })
    }
    if (this.worker) {
      this.worker.terminate()
    }
  }
  clear() {
    this.worker.postMessage({
      command: 'clear',
    })
  }
  encodeSpeex(cb, buffer) {
    cb = cb || this.config.callback
    if (!cb) throw new Error('Callback not set')
    this.callbacks.encodeSpeex.push(cb)
    this.worker.postMessage({
      command: 'encodeSpeex',
      buffer: buffer,
    })
  }
  getConfig() {
    return {
      ...this.config,
    }
  }
  getBuffer(cb) {
    cb = cb || this.config.callback
    if (!cb) throw new Error('Callback not set')
    this.callbacks.getBuffer.push(cb)
    this.worker.postMessage({
      command: 'getBuffer',
    })
  }
  exportWAVAndUpload(url, params = {}, callback) {
    this.exportWAV(function(blob) {
      var fd = new FormData()
      fd.append('speech', blob)
      let sid = params.sid || new Date().getTime()
      fd.append('sid', sid)
      let sound_xml = params.sound_xml || ''
      fd.append('sound_xml', sound_xml);

      var xhr = new XMLHttpRequest()
      if (callback) {
        // xhr.upload.addEventListener(
        //   'progress',
        //   function(e) {
        //     callback('uploading', e)
        //   },
        //   false
        // )
        xhr.addEventListener(
          'load',
          function (e) {
            console.log(e)
            if (xhr.status === 200) {
              // 如果响应成功，获取返回值
              const response = xhr.responseText;
              callback(response); // 调用回调函数并传递响应数据
            } else {
              callback('error', xhr.statusText); // 请求失败，返回状态码或错误信息
            }
          },
          false
        );
        xhr.addEventListener(
          'error',
          function(e) {
            callback('error', e)
          },
          false
        )
        xhr.addEventListener(
          'abort',
          function(e) {
            callback('cancel', e)
          },
          false
        )
      }
      xhr.open('POST', url)
      xhr.setRequestHeader('Accept', 'application/json'); // 设置请求头，如果服务器返回的是JSON
      xhr.responseType = 'text'; // 根据需要设置响应类型，默认是 'text'
      xhr.send(fd)
    })
  }

  exportWAV(cb) {
    const mimeType = 'audio/wav'
    cb = cb || this.config.callback
    if (!cb) throw new Error('Callback not set')

    this.callbacks.exportWAV.push(cb)
    this.worker.postMessage({
      command: 'exportWAV',
      type: mimeType,
    })
  }
  static forceDownload(blob, filename) {
    let url = (window.URL || window.webkitURL).createObjectURL(blob)
    let link = window.document.createElement('a')
    link.href = url
    link.download = filename || 'output.wav'
    let click = document.createEvent('Event')
    click.initEvent('click', true, true)
    link.dispatchEvent(click)
    console.log(link)
  }

  static createRecorder(config) {
    return new Promise((resolve, reject) => {
      // 老的浏览器可能根本没有实现 mediaDevices，所以我们可以先设置一个空的对象
      if (navigator.mediaDevices === undefined) {
        navigator.mediaDevices = {}
      }

      // 一些浏览器部分支持 mediaDevices。我们不能直接给对象设置 getUserMedia
      // 因为这样可能会覆盖已有的属性。这里我们只会在没有getUserMedia属性的时候添加它。
      if (navigator.mediaDevices.getUserMedia === undefined) {
        navigator.mediaDevices.getUserMedia = function(constraints) {
          // 首先，如果有getUserMedia的话，就获得它
          var getUserMedia =
            navigator.getUserMedia ||
            navigator.webkitGetUserMedia ||
            navigator.mozGetUserMedia

          // 一些浏览器根本没实现它 - 那么就返回一个error到promise的reject来保持一个统一的接口
          if (!getUserMedia) {
            return Promise.reject(
              new Error(i18n.tc('media_devices_unsupported_browserLabel'))
            )
          }

          // 否则，为老的navigator.getUserMedia方法包裹一个Promise
          return new Promise(function(resolve, reject) {
            getUserMedia.call(navigator, constraints, resolve, reject)
          })
        }
      }

      if (navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices
          .getUserMedia({
            audio: true,
          })
          .then(stream => {
            const AudioContext =
              window.AudioContext || window.webkitAudioContext
            const audioCtx = new AudioContext()
            const input = audioCtx.createMediaStreamSource(stream)
            const rec = new Recorder(input, config)
            resolve(rec)
          })
          .catch(error => {
            console.error(error)
            reject(this.__getUserMediaError(error))
          })
      } else {
        reject({
          message: i18n.tc('media_devices_unsupported_browserLabel'),
        })
      }
    })
  }
  static __settingMediaDevices(navigator) {
    navigator.mediaDevices
      .enumerateDevices()
      .then(e => {
        console.log(e)
        e.map((d, i) => {
          console.log(d, i)
        })
      })
      .catch(error => {
        console.log(error)
      })
  }
  static __getUserMediaError(error) {
    let message = error.message
    let title
    //https://developer.mozilla.org/zh-CN/docs/Web/API/MediaDevices/getUserMedia
    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
      case 'NotFoundError':
        title = i18n.tc('media_devices_help_title')
        message = i18n.tc('media_devices_help_content')
        break
      case 'NotReadableError':
        message = i18n.tc('layer_common_notfoundmic')
        break
      default:
        message = error.name + error.message
        break
    }
    return {
      title: title,
      message: message,
    }
  }
}

export default Recorder
